import React from 'react';
import autobind from 'autobind';
import { observer, inject } from 'mobx-react';
import { observable, makeObservable } from 'mobx';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { Ehr } from '@patient/store/ehr/Ehr';
import { PatientStore } from '@patient/store/patient/PatientStore';
import { patientService } from '@patient/services/patient/patientService';
import { EhrPageNames } from '@patient/store/constants';
import { OptionType } from '@components/Select';
import { RepresentativeCard, RepresentativeCardProps, RepresentativeCardType } from './RepresentativeCard';
import { RepresentativeType } from 'chaika-enums';
import { translate } from '@chaika/i18n/helper';
import { getRepresentativeTypeTranslation } from '@utils/enumTranslation';
import { assertIsDefined } from '@utils/assertIsDefined';

interface Props extends RepresentativeCardProps {
    onCreateNewEhrClick?(): void;
    onUpdateRepresentativePerson?(oldId: string, newId: string): void;
    onCreateRepresentativePerson?(representativeId: string, type: RepresentativeType): void;
    onConfirmRepresentative?(representativeId: string): void;
    onCancelCreate?(): void;
    onRemove?(representativeId: string): void;
    onRepresentativeTypeChange(representativeId: string, type: RepresentativeType): void;
}

interface StoreProps {
    ehr: Ehr;
    patientStore: PatientStore;
}

const injectableStores: (keyof StoreProps)[] = [Ehr.Name, PatientStore.Name];

const CREATE_MODE_HANDLER_MESSAGE = 'RepresentativeList passes create handlers to the create mode card';
const EXISTING_CARD_HANDLER_MESSAGE = 'RepresentativeList passes edit handlers to existing representative cards';
const EXISTING_CARD_ID_MESSAGE = 'Existing representative card is rendered with its representative id';

@observer
class Component extends React.Component<Props & StoreProps> {
    @observable private newRepresentativeId: Nullable<string> = null;
    @observable private type: RepresentativeType = RepresentativeType.Child;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public render(): React.ReactNode {
        const { cardType, representativeId, createMode, ehr, patientStore } = this.props;

        const representative = !representativeId
            ? undefined
            : cardType === RepresentativeCardType.Representative
              ? ehr.representativeList.get(representativeId)
              : ehr.confidantList.get(representativeId);
        const representativePerson = representativeId ? patientStore.patientList.get(representativeId) : undefined;

        return React.createElement(RepresentativeCard, {
            cardType,
            createMode,
            ehrId: ehr.id,
            status: createMode ? undefined : representative?.status,
            type: createMode ? this.type : representative?.type,
            representativeId: createMode ? this.newRepresentativeId : representativeId,
            representativeInfo: `${representativePerson?.fullInfo ?? ''} ${translate('relationshipType')}: ${
                representative?.type ? getRepresentativeTypeTranslation(representative.type) : ''
            }`,
            onRepresentativeClick: this.handleRepresentativeClick,
            onConfirmRepresentative: this.handleConfirmRepresentative,
            onPatientChange: this.handlePatientChange,
            onSaveClick: this.handleSaveClick,
            onCreateNewEhrClick: this.handleCreateNewEhrClick,
            onRemoveClick: this.handleRemoveClick,
            onCancelCreate: this.handleCancelCreate,
            onTypeChange: this.onTypeChange,
        });
    }

    @autobind
    private handleRepresentativeClick(): void {
        const { representativeId, patientStore } = this.props;
        assertIsDefined(representativeId, EXISTING_CARD_ID_MESSAGE);

        const representativePerson = patientStore.patientList.get(representativeId);

        const url = `/patient/${representativePerson?.recordId}?currentPage=${EhrPageNames.MedicalCaseList}`;

        window.open(url, '_blank');
    }

    @autobind
    private async handleSaveClick(): Promise<void> {
        assertIsDefined(this.props.onCreateRepresentativePerson, CREATE_MODE_HANDLER_MESSAGE);
        assertIsDefined(this.newRepresentativeId, 'Assign button is enabled only after a representative is selected');
        await this.props.onCreateRepresentativePerson(this.newRepresentativeId, this.type);
    }

    @autobind
    private handleCreateNewEhrClick(): void {
        assertIsDefined(this.props.onCreateNewEhrClick, CREATE_MODE_HANDLER_MESSAGE);
        this.props.onCreateNewEhrClick();
    }

    @autobind
    private async handleConfirmRepresentative(): Promise<void> {
        assertIsDefined(this.props.onConfirmRepresentative, EXISTING_CARD_HANDLER_MESSAGE);
        assertIsDefined(this.props.representativeId, EXISTING_CARD_ID_MESSAGE);
        await this.props.onConfirmRepresentative(this.props.representativeId);
    }

    @autobind
    private async handleRemoveClick(): Promise<void> {
        assertIsDefined(this.props.onRemove, EXISTING_CARD_HANDLER_MESSAGE);
        assertIsDefined(this.props.representativeId, EXISTING_CARD_ID_MESSAGE);
        await this.props.onRemove(this.props.representativeId);
    }

    @autobind
    private async handleCancelCreate(): Promise<void> {
        this.props.onCancelCreate?.();
    }

    @autobind
    private async handlePatientChange({ value }: OptionType<string>): Promise<void> {
        const { createMode, onUpdateRepresentativePerson, representativeId } = this.props;

        if (createMode) {
            this.newRepresentativeId = value;
            await patientService.loadByIds([value]);
        } else {
            assertIsDefined(onUpdateRepresentativePerson, EXISTING_CARD_HANDLER_MESSAGE);
            assertIsDefined(representativeId, EXISTING_CARD_ID_MESSAGE);
            await onUpdateRepresentativePerson(representativeId, value);
        }
    }

    @autobind
    private async onTypeChange({ value }: OptionType<RepresentativeType>): Promise<void> {
        const { createMode, onRepresentativeTypeChange, representativeId } = this.props;

        if (createMode) {
            this.type = value;
        } else {
            assertIsDefined(representativeId, EXISTING_CARD_ID_MESSAGE);
            await onRepresentativeTypeChange(representativeId, value);
        }
    }
}

export const RepresentativeCardContainer = inject<Props, StoreProps>(...injectableStores)(Component);
